import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { UserMenuDropdown } from "@/components/dashboard/UserMenuDropdown";
import { NewsStrip } from "@/components/dashboard/desk/NewsStrip";
import { useBiasAccent } from "@/components/dashboard/command/useBiasAccent";
import { useGoldPrice } from "@/hooks/useGoldPrice";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardLayout,
});

const PAGE_LABELS: { prefix: string; title: string; subtitle: string }[] = [
  
  { prefix: "/dashboard/macrolens", title: "XAU MacroLens™", subtitle: "Macro & Technical Confluence" },
  
  { prefix: "/dashboard/academy", title: "Academy", subtitle: "Institutional Curriculum" },
  { prefix: "/dashboard/ebooks", title: "Library", subtitle: "Playbooks & Guides" },
  { prefix: "/dashboard/calendar", title: "Economic Calendar", subtitle: "High-Impact Macro Schedule" },
  { prefix: "/dashboard/tools", title: "Risk Planner", subtitle: "Position Sizing & Parameters" },
  { prefix: "/dashboard/analyst", title: "StructureLens", subtitle: "Institutional SMC Engine" },
  { prefix: "/dashboard/profile", title: "Profile", subtitle: "Account & Preferences" },
  { prefix: "/dashboard/support", title: "Support Desk", subtitle: "Subscriber Assistance" },
  { prefix: "/dashboard/case-studies", title: "Case Studies", subtitle: "Audited Replays" },
  { prefix: "/dashboard/journal", title: "Journal", subtitle: "Execution Workspace" },
  { prefix: "/dashboard", title: "XAU Terminal™", subtitle: "Institutional Gold Workstation" },
];

function resolvePageLabel(pathname: string) {
  const match = PAGE_LABELS.find((entry) =>
    pathname === entry.prefix || pathname.startsWith(entry.prefix + "/")
  );
  return match ?? PAGE_LABELS[PAGE_LABELS.length - 1];
}

function DashboardLayout() {
  const location = useLocation();
  const { accent } = useBiasAccent();
  const { title, subtitle } = resolvePageLabel(location.pathname);
  const [scrolled, setScrolled] = useState(false);
  const { price, direction } = useGoldPrice(60_000);

  // The page scrolls on the window, not inside <main>, so the shrink
  // trigger must listen there. rAF-throttled to keep the transition smooth.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // Shrink once the top hero card (Market Climate in the Trading Room)
        // has scrolled out; hysteresis avoids flicker near the threshold.
        setScrolled((prev) => (prev ? window.scrollY > 180 : window.scrollY > 240));
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The Trading Room integrates the news strip into its own command card,
  // so the floating banner is suppressed there to keep the session label clear.
  const isTradingRoom = location.pathname.startsWith("/dashboard/trading-room");

  return (
    <SidebarProvider>
      <div
        className="flex min-h-screen w-full max-w-full overflow-x-clip bg-background"
        style={{ ["--orion-accent" as string]: accent }}
      >

        <DashboardSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          {/*
            Borderless frosted-glass header. No lights are painted here: the
            bar is a pure glass plate, so the market-climate glow the page
            already renders passes through it while content slides underneath.
            A faint top bevel is the only decoration. On scroll the bar
            shrinks (h-16 to h-12), the subtitle fades, and the glass
            densifies slightly so text stays legible beneath it.
          */}
          <header
            className={`sticky top-0 z-40 flex items-center gap-3 px-4 backdrop-blur-xl transition-[height,background-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              scrolled ? "h-12 bg-background/50" : "h-16 bg-background/30"
            }`}
            style={{
              boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.06)",
            }}
          >
            <SidebarTrigger />
            <div className="flex min-w-0 flex-col leading-tight">
              <div className="grid">
                <div
                  className={`col-start-1 row-start-1 truncate font-medium transition-[opacity,transform,font-size] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    scrolled && price != null
                      ? "pointer-events-none -translate-y-1 text-xs opacity-0"
                      : scrolled
                        ? "text-xs"
                        : "text-sm"
                  }`}
                  aria-hidden={scrolled && price != null}
                >
                  {title}
                </div>
                <div
                  className={`col-start-1 row-start-1 flex items-baseline gap-2 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    scrolled && price != null ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-1 opacity-0"
                  }`}
                  aria-hidden={!(scrolled && price != null)}
                >
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                    XAU/USD
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                    {price != null ? price.toFixed(2) : ""}
                  </span>
                  {(direction === "up" || direction === "down") && (
                    <span
                      className={`font-mono text-[10px] ${
                        direction === "up" ? "text-emerald" : "text-destructive"
                      }`}
                    >
                      {direction === "up" ? "▲" : "▼"}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{ gridTemplateRows: scrolled ? "0fr" : "1fr", opacity: scrolled ? 0 : 1 }}
              >
                <div className="min-h-0 overflow-hidden font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                  {subtitle}
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center">
              <UserMenuDropdown compact={scrolled} />
            </div>
          </header>
          <main className="flex-1 overflow-x-hidden">
            {!isTradingRoom && (
              <div className="mx-1 empty:hidden md:mx-2">
                <NewsStrip />
              </div>
            )}
            <div key={location.pathname} className="animate-fade-in">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

